import { Inject, Injectable, OnDestroy, Optional } from '@angular/core';
import { API_BASE_URL } from '@vsolv/dev-kit/ngx';
import { BehaviorSubject, Subscription, combineLatest, distinctUntilChanged, fromEvent, map, shareReplay } from 'rxjs';
import { CONNECTIVITY_CONFIG } from '../connectivity.config';

/**
 * Tracks whether the app can actually reach the outside world.
 *
 * Two signals feed into it:
 *  - the browser's own online/offline events (link layer went away)
 *  - failed api requests reported by the ConnectivityInterceptor (link is up but we can't reach the api)
 *
 * The second one matters because `navigator.onLine` stays true on captive portals, vpn drops and api outages.
 */
@Injectable()
export class ConnectivityService implements OnDestroy {
  private readonly browserOnline$ = new BehaviorSubject(navigator.onLine);
  private readonly apiReachable$ = new BehaviorSubject(true);

  private readonly subscriptions = new Subscription();
  private probeTimer?: ReturnType<typeof setInterval>;

  readonly online$ = combineLatest([this.browserOnline$, this.apiReachable$]).pipe(
    map(([browserOnline, apiReachable]) => browserOnline && apiReachable),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: false })
  );

  constructor(@Optional() @Inject(API_BASE_URL) private baseUrl: string | null) {
    this.subscriptions.add(
      fromEvent(window, 'online').subscribe(() => {
        this.browserOnline$.next(true);
        if (!this.apiReachable$.value) this.startProbing();
      })
    );

    this.subscriptions.add(
      fromEvent(window, 'offline').subscribe(() => {
        this.browserOnline$.next(false);
        // Nothing to probe while the browser itself knows we're offline - the online event will tell us.
        this.stopProbing();
      })
    );
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
    this.stopProbing();
  }

  get online() {
    return this.browserOnline$.value && this.apiReachable$.value;
  }

  /** Called by the interceptor when an api request fails without ever reaching the server. */
  reportApiUnreachable() {
    if (!this.apiReachable$.value) return;

    this.apiReachable$.next(false);
    this.startProbing();
  }

  /** Called by the interceptor whenever the server answers, whatever the status code. */
  reportApiReachable() {
    if (this.apiReachable$.value) return;

    this.apiReachable$.next(true);
    this.stopProbing();
  }

  /**
   * The browser only fires `online` when the link comes back, so an api-level outage would leave the toast up
   * forever. Poll the api until something answers.
   */
  private startProbing() {
    if (this.probeTimer) return;

    this.probeTimer = setInterval(() => this.probe(), CONNECTIVITY_CONFIG.probeInterval);
  }

  private stopProbing() {
    if (!this.probeTimer) return;

    clearInterval(this.probeTimer);
    this.probeTimer = undefined;
  }

  private async probe() {
    if (!this.browserOnline$.value) return;

    try {
      // no-cors so a cors rejection isn't mistaken for being offline - we only care that the server answered.
      await fetch(this.baseUrl ?? '/', { method: 'HEAD', mode: 'no-cors', cache: 'no-store' });
      this.reportApiReachable();
    } catch {
      // Still unreachable, keep the toast up and try again on the next tick.
    }
  }
}
